import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtEUR, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useT } from "@/context/I18nContext";

export default function Checkout() {
  const { items, subtotal, clear, identify } = useCart();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [rates, setRates] = useState([]);
  const [country, setCountry] = useState("CY");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [isSubscription, setIsSubscription] = useState(false);
  const [frequency, setFrequency] = useState("month_1"); // week_1, week_2, month_1, month_2
  const [giftCode, setGiftCode] = useState("");
  const [gift, setGift] = useState(null); // {code, remaining_amount_eur}
  const [useStoreCredit, setUseStoreCredit] = useState(false);
  const [ref, setRef] = useState(null);
  const [form, setForm] = useState({
    email: user?.email || "", name: user?.name || "", phone: "",
    sex: "", dob: "",
    line1: "", line2: "", city: "", postal_code: "", full_name: user?.name || "",
    notes: "",
  });

  useEffect(() => {
    api.get("/shipping/rates").then((r) => setRates(r.data));
    const bundleCode = localStorage.getItem("brunchi_bundle_code");
    if (bundleCode && !promoCode) setPromoCode(bundleCode);
    // Auto-apply REFERRAL10 if user arrived via /r/CODE
    const refCode = localStorage.getItem("brunchi_referrer_code");
    if (refCode && !promoCode && !bundleCode) setPromoCode("REFERRAL10");
    if (user) api.get("/referrals/me").then((r) => setRef(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  // Sync auth-resolved user's identity into the form once AuthContext hydrates
  // (initial useState runs before /me returns, leaving fields blank for signed-in users).
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      email: f.email || user.email || "",
      name: f.name || user.name || "",
      full_name: f.full_name || user.name || "",
    }));
  }, [user]);

  const applyGift = async () => {
    if (!giftCode.trim()) return;
    try {
      const { data } = await api.post("/gift-cards/lookup", { code: giftCode.trim().toUpperCase() });
      setGift(data);
      toast.success(`Gift card · ${data.remaining_amount_eur.toFixed(2)}€ balance`);
    } catch (err) {
      setGift(null);
      toast.error(apiError(err, "Gift card not found"));
    }
  };

  useEffect(() => {
    if (!subtotal) return;
    api.post("/shipping/quote", { country_code: country, subtotal_eur: subtotal, city: form.city || null })
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [country, subtotal, form.city]);

  // Revalidate promo when subtotal changes
  useEffect(() => {
    if (!promo) return;
    api.post("/checkout/apply-code", { code: promo.code, subtotal_eur: subtotal })
      .then((r) => setPromo(r.data))
      .catch(() => { setPromo(null); toast.info("Promo code no longer valid"); });
  }, [subtotal]);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setApplyingPromo(true);
    try {
      const { data } = await api.post("/checkout/apply-code", {
        code: promoCode.trim().toUpperCase(), subtotal_eur: subtotal,
      });
      setPromo(data);
      toast.success(`Code ${data.code} applied · −${fmtEUR(data.amount_off_eur)}`);
    } catch (err) {
      setPromo(null);
      toast.error(apiError(err, "Invalid code"));
    } finally { setApplyingPromo(false); }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="font-serif text-2xl mb-2">{t("checkout.empty")}</div>
        <button onClick={() => nav("/shop")} className="mt-4 text-[color:var(--brunchi-terracotta)] underline">{t("checkout.continue_shopping")}</button>
      </div>
    );
  }

  const shipCost = quote?.shipping_cost_eur || 0;
  const discount = promo?.amount_off_eur || 0;
  const total = Math.max(0, subtotal + shipCost - discount);
  const canSubmit = quote?.meets_minimum && form.email && form.name && form.phone && form.line1 && form.city && form.postal_code;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const commonPayload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: { email: form.email, name: form.name, phone: form.phone, sex: form.sex || null, dob: form.dob || null },
        shipping_address: {
          full_name: form.full_name || form.name, line1: form.line1, line2: form.line2,
          city: form.city, postal_code: form.postal_code, country, phone: form.phone,
        },
        origin_url: window.location.origin,
      };
      if (isSubscription) {
        if (!user) {
          toast.info(t("checkout.subs_sign_in_required"));
          nav("/auth?next=/checkout");
          setSubmitting(false);
          return;
        }
        const [interval, count] = frequency.split("_");
        const { data } = await api.post("/subscriptions/checkout", {
          ...commonPayload, interval, interval_count: parseInt(count),
        });
        window.location.href = data.checkout_url;
      } else {
        const referrer = localStorage.getItem("brunchi_referrer_code");
        const payload = {
          ...commonPayload, notes: form.notes,
          promo_code: promo?.code || null,
          referrer_code: referrer || null,
          gift_card_code: gift?.code || null,
          use_store_credit: useStoreCredit,
        };
        const { data } = await api.post("/checkout", payload);
        localStorage.removeItem("brunchi_bundle_code");
        localStorage.removeItem("brunchi_referrer_code");
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(apiError(err, t("checkout.failed")));
      setSubmitting(false);
    }
  };

  const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brunchi-terracotta)]";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="checkout-page">
      <h1 className="font-serif text-3xl lg:text-4xl mb-8">{t("checkout.title")}</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={onSubmit} className="lg:col-span-2 space-y-6" data-testid="checkout-form">
          <section className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-6">
            <div className="font-serif text-xl mb-4">{t("checkout.contact")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required data-testid="checkout-email" placeholder={t("checkout.email")} type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                onBlur={(e) => { if (/.+@.+\..+/.test(e.target.value)) identify(e.target.value, form.name); }} className={input} />
              <input required data-testid="checkout-name" placeholder={t("checkout.name")} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
              <input required data-testid="checkout-phone" placeholder={t("checkout.phone")} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} />
              <select data-testid="checkout-sex" value={form.sex}
                onChange={(e) => setForm({ ...form, sex: e.target.value })} className={input}>
                <option value="">{t("checkout.sex_optional")}</option>
                <option value="female">{t("checkout.sex_female")}</option>
                <option value="male">{t("checkout.sex_male")}</option>
                <option value="other">{t("checkout.sex_other")}</option>
              </select>
              <input data-testid="checkout-dob" placeholder={t("checkout.dob")} type="date"
                value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className={input} />
            </div>
          </section>

          <section className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-6">
            <div className="font-serif text-xl mb-4">{t("checkout.address")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <select required data-testid="checkout-country" value={country}
                onChange={(e) => setCountry(e.target.value)} className={input}>
                {rates.map((r) => (
                  <option key={r.country_code} value={r.country_code}>
                    {t(`country.${r.country_code}`) !== `country.${r.country_code}` ? t(`country.${r.country_code}`) : r.country_name}
                  </option>
                ))}
              </select>
              <input required data-testid="checkout-city" placeholder={t("checkout.city")} value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} className={input} />
              <input required data-testid="checkout-line1" placeholder={t("checkout.street")} value={form.line1}
                onChange={(e) => setForm({ ...form, line1: e.target.value })} className={`${input} sm:col-span-2`} />
              <input data-testid="checkout-line2" placeholder={t("checkout.apartment")} value={form.line2}
                onChange={(e) => setForm({ ...form, line2: e.target.value })} className={`${input} sm:col-span-2`} />
              <input required data-testid="checkout-postal" placeholder={t("checkout.postal")} value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className={input} />
            </div>
            <textarea data-testid="checkout-notes" placeholder={t("checkout.notes")}
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={`${input} mt-3`} rows={2} />
          </section>

          <button type="submit" disabled={!canSubmit || submitting}
            data-testid="checkout-submit-btn"
            className="w-full py-4 rounded-full pill-primary font-medium disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? t("checkout.redirecting")
              : isSubscription
                ? t("checkout.start_standing", { total: fmtEUR(total), freq: t(`checkout.freq.${frequency}`) })
                : t("checkout.pay_with_stripe", { total: fmtEUR(total) })}
          </button>
          {!quote?.meets_minimum && (
            <div className="text-sm text-[color:var(--brunchi-terracotta)] text-center" data-testid="min-order-warning">
              {t("checkout.min_order", { amount: fmtEUR(quote?.min_order_eur || 15) })}
            </div>
          )}
        </form>

        <aside className="bg-[color:var(--brunchi-cream)] rounded-xl p-6 h-fit sticky top-24" data-testid="checkout-summary">
          <div className="font-display text-xl mb-4">{t("checkout.summary")}</div>
          <div className="space-y-3 mb-4">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <div>{i.quantity} × {i.name}</div>
                <div className="font-mono">{fmtEUR(i.price_eur * i.quantity)}</div>
              </div>
            ))}
          </div>

          {/* Standing order toggle */}
          <div className="border-t border-[color:var(--brunchi-stone)] pt-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="standing-order-toggle-label">
              <input type="checkbox" checked={isSubscription} onChange={(e) => setIsSubscription(e.target.checked)}
                className="mt-1 accent-[color:var(--brunchi-green)]" data-testid="standing-order-toggle" />
              <div>
                <div className="font-display font-semibold text-sm">{t("checkout.standing_toggle")}</div>
                <div className="text-xs text-[color:var(--brunchi-ink-soft)] mt-0.5">{t("checkout.standing_help")}</div>
              </div>
            </label>
            {isSubscription && !user && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="subs-auth-notice">
                {t("checkout.subs_sign_in_required")}
                <Link to="/auth?next=/checkout" className="underline ml-1 font-medium" data-testid="subs-sign-in-link">
                  {t("auth.sign_in")}
                </Link>
              </div>
            )}
            {isSubscription && (
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
                className="mt-3 w-full px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm" data-testid="standing-order-frequency">
                <option value="week_1">{t("checkout.freq.week_1")}</option>
                <option value="week_2">{t("checkout.freq.week_2")}</option>
                <option value="month_1">{t("checkout.freq.month_1")}</option>
                <option value="month_2">{t("checkout.freq.month_2")}</option>
              </select>
            )}
          </div>
          <div className="border-t border-[color:var(--brunchi-stone)] pt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span>{t("checkout.subtotal")}</span><span className="font-mono">{fmtEUR(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t("checkout.shipping")} ({quote?.country_name || country}{form.city ? `, ${form.city}` : ""})</span>
              <span className="font-mono">
                {quote?.shipping_cost_eur === 0 ? t("checkout.free") : fmtEUR(quote?.shipping_cost_eur || 0)}
              </span>
            </div>
            {promo && (
              <div className="flex justify-between text-[color:var(--brunchi-success)]">
                <span>{t("checkout.discount")} ({promo.code})</span>
                <span className="font-mono">−{fmtEUR(discount)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-base pt-2 border-t border-[color:var(--brunchi-stone)]">
              <span>{t("checkout.total")}</span><span className="font-mono text-[color:var(--brunchi-terracotta)]" data-testid="checkout-total">{fmtEUR(total)}</span>
            </div>
          </div>

          {/* Promo code */}
          <div className="mt-5 border-t border-[color:var(--brunchi-stone)] pt-4">
            {promo ? (
              <div className="flex items-center justify-between text-sm bg-white rounded-lg border border-[color:var(--brunchi-success)]/30 px-3 py-2">
                <span className="font-mono font-semibold text-[color:var(--brunchi-success)]" data-testid="promo-applied">{promo.code}</span>
                <button onClick={() => { setPromo(null); setPromoCode(""); }} data-testid="promo-remove-btn"
                  className="text-xs underline">{t("checkout.remove")}</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={promoCode} onChange={(e) => setPromoCode(e.target.value)}
                  placeholder={t("checkout.promo_placeholder")} data-testid="promo-input"
                  className="flex-1 px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm uppercase font-mono" />
                <button onClick={applyPromo} disabled={applyingPromo || !promoCode}
                  data-testid="promo-apply-btn"
                  className="px-4 py-2 rounded-lg pill-outline text-sm disabled:opacity-40">
                  {t("checkout.apply")}
                </button>
              </div>
            )}
          </div>

          {/* Gift card */}
          <div className="mt-3">
            {gift ? (
              <div className="flex items-center justify-between text-sm bg-white rounded-lg border border-[color:var(--brunchi-green)]/40 px-3 py-2">
                <span className="font-mono font-semibold text-[color:var(--brunchi-green-hover)]" data-testid="gift-applied">
                  {gift.code} · {fmtEUR(gift.remaining_amount_eur)}
                </span>
                <button onClick={() => { setGift(null); setGiftCode(""); }} className="text-xs underline">{t("checkout.remove")}</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={giftCode} onChange={(e) => setGiftCode(e.target.value)}
                  placeholder={t("checkout.gift_placeholder")} data-testid="gift-input"
                  className="flex-1 px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm uppercase font-mono" />
                <button onClick={applyGift} disabled={!giftCode}
                  data-testid="gift-apply-btn"
                  className="px-4 py-2 rounded-lg pill-outline text-sm disabled:opacity-40">
                  {t("checkout.apply")}
                </button>
              </div>
            )}
          </div>

          {/* Store credit */}
          {ref && ref.store_credit_eur > 0 && (
            <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={useStoreCredit} onChange={(e) => setUseStoreCredit(e.target.checked)}
                className="accent-[color:var(--brunchi-green)]" data-testid="use-store-credit" />
              {t("checkout.use_store_credit")} ({fmtEUR(ref.store_credit_eur)})
            </label>
          )}

          {quote && (
            <div className="mt-4 text-xs text-[color:var(--brunchi-ink-soft)]">
              {t("checkout.estimated", { days: quote.estimated_days })}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
